import { useEffect, useState } from "react";
import axiosInstance from "../../../api/axiosInstance";
import {
  TEXT_RULES,
  REPEATER_RULES,
  IMAGE_SPECS,
  validateText,
  validateUrl,
  validateByRules,
  validateRepeaterCount,
  validateImageBeforeUpload,
} from "./Validation";
import Editor from "../../../components/RichTextEditor/Editor"

// const defaults = {
//   hero: {
//     section_badge: "AI-Powered Procurement Platform",
//     section_title: "Procure Smarter. Source Globally. Powered by AI.",
//     section_description: "Find verified suppliers worldwide, compare quotations with AI, track deliveries in real time, and complete every transaction securely.",
//     actions: [],
//     buyer_card: {
//       title: "For Buyers",
//       items: [],
//       cta: { label: "", url: "" },
//     },
//     supplier_card: {
//       title: "For Suppliers",
//       items: [],
//       cta: { label: "", url: "" },
//     },
//   },
//   trusted_brands: {
//     section_title: "Brands That Trust Us",
//     section_description: "",
//     stats: [],
//     highlights: [],
//   },
//   working: {
//     section_title: "How Procman Works",
//     steps: [],
//   },
//   buyer_supplier: {
//     section_title: "Built for Buyers and Suppliers",
//     section_description: "",
//     buyer_card: { title: "For Buyers", cta: { label: "", url: "" }, features: [] },
//     supplier_card: { title: "For Suppliers", cta: { label: "", url: "" }, features: [] },
//   },
//   platform_features: {
//     section_badge: "PLATFORM FEATURES",
//     section_title: "Everything You Need to Manage Procurement",
//     section_description: "",
//     features: [],
//   },
//   comparision: {
//     section_badge: "WHY PROCMAN",
//     section_title: "Traditional Procurement vs Procman",
//     procman: { title: "Procman", points: [] },
//     traditional: { title: "Traditional", points: [] },
//   },
//   enterprise_security: {
//     section_title: "Enterprise Grade Security",
//     security_features: [],
//   },
//   industries: {
//     section_title: "Procurement Solutions for Every Industry",
//     items: [],
//   },
//   case_studies: {
//     section_title: "Real Business Results",
//     section_description: "A running log of teams who put Procman into production and what changed.",
//   },
//   testimonials: {
//     section_title: "Trusted by modern procurement teams globally.",
//     section_description: "Built directly from operational bottlenecks to deliver market speed, strict compliance, and transparent multi-vendor bidding.",
//   },
//   faq: {
//     section_title: "Frequently Asked Questions",
//     faqs: [],
//   },
//   cta: {
//     section_title: "Ready to Transform Your Procurement?",
//     section_description: "",
//     primary_cta: { label: "Create RFQ Free", url: "/create-rfq" },
//     secondary_cta: { label: "Book a Demo", url: "/book-demo" },
//     benefits: [],
//   },
//   seo: {
//     meta_title: "",
//     meta_description: "",
//     meta_keywords: "",
//     canonical_url: "https://procman.ai",
//     og_title: "",
//     og_description: "",
//     seo_content: ""
//   },
//   schema: {
//     custom_json: "",
//     additional_schema_type: "",
//     additional_schema_data: {},
//   },
//   hero_background_image: null,
//   hero_desktop_dashboard_image: null,
//   hero_mobile_dashboard_image: null,
//   buyer_card_image: null,
//   supplier_card_image: null,
//   security_feature_1_image: null,
//   security_feature_2_image: null,
//   security_feature_3_image: null,
//   og_image: null,
//   twitter_image: null,
// };

const defaults = {
  hero: {
    section_badge: "AI-Powered Procurement Platform ",
    section_title: "Procure Smarter. Source Globally. Powered by AI.",
    section_description: "Find verified suppliers worldwide, compare quotations with AI, track deliveries in real time, and complete every transaction securely.",
    actions: [
      {
        label: "Browse Suppliers",
        url: "/vendors",
        variant: "primary",
      },
      {
        label: "Explore RFQs",
        url: "/public-rfqs",
        variant: "secondary",
      },
    ],
    buyer_card: {
      title: "For Buyers",
      items: [
        { label: "AI Price Comparison" },
        { label: "Verified Global Suppliers" },
        { label: "Smart AI Recommendations" },
        { label: "Live Delivery Tracking" },
      ],
      cta: {
        label: "Start Sourcing",
        url: "/login",
      },
    },
    supplier_card: {
      title: "For Suppliers",
      items: [
        { label: "Guaranteed Payments" },
        { label: "Multiple RFQ Opportunities" },
        { label: "Verified Enterprise Buyers" },
        { label: "Logistics Managed by Procman" },
      ],
      cta: {
        label: "Become a Supplier",
        url: "/vendor/register",
      },
    },
  },

  trusted_brands: {
    section_title: "Brands That Trust Us",
    section_description: "",
    stats: [
      { number: "5000+", label: "Verified Suppliers" },
      { number: "25+", label: "Industries" },
      { number: "15+", label: "States Served" },
      { number: "100%", label: "Digital Procurement" },
    ],
    highlights: [
      { label: "GST Ready" },
      { label: "KYC Verified" },
      { label: "AI Powered" },
      { label: "Secure Payments" },
      { label: "End-to-End Procurement" },
    ],
  },

  working: {
    section_title: "How Procman Works – End to End Workflow",
    section_description: "",
    steps: [
      {
        title: "Create RFQ",
        description:
          "Define requirements, specifications, quantities, and timelines.",
      },
      {
        title: "AI Supplier Discovery ",
        description: "Discover suppliers using AI.",
      },
      {
        title: "RFQ Invitation & Follow-Ups",
        description:
          "Send RFQs to suppliers and automate reminders for responses via Email and WhatsApp.",
      },
      {
        title: "Quote Submission",
        description:
          "Suppliers submit quotations digitally in a standardized and structured format.",
      },
      {
        title: "AI-Powered Quote Comparison",
        description:
          "Automatically compare pricing, delivery, quality, payment terms and past performance.",
      },
      {
        title: "Shortlisting & Negotiation",
        description:
          "Shortlist the best suppliers and conduct digital negotiations at item or quote level.",
      },
      {
        title: "Approval Workflow",
        description:
          "Route decisions through automated multi-level approvals based on your corporate policies.",
      },
      {
        title: "Purchase Order Creation",
        description:
          "Generate purchase orders based on your approval workflow and settings.",
      },
      {
        title: "Delivery & Payment Management",
        description:
          "Track deliveries, confirm receipt, and process supplier payments seamlessly.",
      },
    ],
  },

  buyer_supplier: {
    section_title: "Built for Buyers and Suppliers",
    section_description: "Manage procurement from RFQ to payment.",
    buyer_card: {
      title: "For Buyers",
      cta: {
        label: "Create RFQ",
        url: "/create-rfq",
      },
      features: [
        "AI Bid Comparison",
        "Itemwise Price Comparison",
        "Worldwide Supplier Network",
        "Get Verified Suppliers",
        "AI smart recommendations based on delivery time and quotations",
        "Real-Time Delivery Tracking",
      ],
    },
    supplier_card: {
      title: "For Suppliers",
      cta: {
        label: "Browse Live RFQs",
        url: "/login",
      },
      features: [
        "100% Payment Assurance",
        "Multiple RFQ Opportunities",
        "Worldwide Buyer Network",
        "Get Verified Buyers",
        "Hassles Free Delivery",
        "Secure Business Transactions",
      ],
    },
  },

  platform_features: {
    section_badge: "PLATFORM FEATURES",
    section_title: "Everything You Need to Manage Procurement",
    section_description:
      "From supplier discovery to final approval — one connected workflow.",
    features: [
      {
        title: "AI Supplier Discovery",
        description:
          "Discover relevant suppliers instantly using AI-powered matching based on your RFQ requirements and criteria.",
      },
      {
        title: "Automated RFQ Follow-Ups",
        description:
          "Increase supplier participation with automated reminders and follow-ups via email and WhatsApp.",
      },
      {
        title: "Flexible Supplier Shortlisting",
        description:
          "Shortlist suppliers automatically based on rankings (L1, L2, L3) or manually select preferred vendors.",
      },
      {
        title: "Intelligent Digital Negotiation",
        description:
          "Negotiate at both item and quotation level with competitive pricing insights and target-price negotiations.",
      },
      {
        title: "Encrypted Bid Protection",
        description:
          "Keep supplier bid data encrypted and confidential until bidding closes to ensure fair competition.",
      },
      {
        title: "Automated Approval Workflows",
        description:
          "Route RFQs, negotiations, and purchase decisions through configurable multi-level approval chains.",
      },
      {
        title: "Contract-Based RFQs",
        description:
          "Create repeat purchase orders using approved supplier contracts and negotiated rates seamlessly.",
      },
      {
        title: "Procurement Analytics Dashboard",
        description:
          "rack cost savings, negotiation outcomes, sourcing efficiency, and supplier performance",
      },
    ],
  },

  comparision: {
    section_badge: "WHY PROCMAN",
    section_title: "Traditional Procurement vs Procman ",
    section_description: "",

    procman: {
      title: "Procman ",
      points: [
        "AI-powered supplier discovery in minutes akak",
        "Automated RFQ management and follow-ups",
        "AI ranks the best quotations in seconds",
        "Faster approvals with streamlined digital workflows",
        "Live tracking from order to payment",
        "Complete procurement history in one platform",
      ],
    },
    traditional: {
      title: "Traditional",
      points: [
        "Finding suppliers takes too much time",
        "Manual RFQ follow-ups waste valuable time",
        "Comparing quotations is slow & error-prone",
        "Lengthy approval cycles delay purchasing",
        "Limited visibility into deliveries and payments",
        "Scattered records make audits difficult",
      ],
    },
  },

  enterprise_security: {
    section_title: "Enterprise Grade Security",
    security_features: [
      {
        title: "KYC Verification",
        description:
          "Verify vendor identities quickly and securely. Prevent fraud and ensure compliance with automated background and legal documentation checks. ",
      },
      {
        title: "Secure Payments",
        description:
          "Execute transactions safely with bank-grade encryption. Support for global payment gateways with multi-layered fraud protection protocols. ",
      },
      {
        title: "Enterprise Data Security",
        description:
          "Keep supplier records, procurement documents, and business information secure with encrypted storage and role-based access controls.",
      },
    ],
  },

  industries: {
    section_title: "Industries We Serve",
  },

  case_studies: {
    section_title: "Real Business Results",
    section_description: "",
  },

  testimonials: {
    section_title: "Trusted by modern procurement teams globally.",
    section_description: "",
  },

  faq: {
    section_title: "Frequently Asked Questions",
    faqs: [
      {
        question: "What is Procman.ai?",
        answer:
          "Procman.ai is an AI-powered procurement platform that helps businesses manage the complete purchasing process—from creating RFQs and finding suppliers to comparing quotations, tracking deliveries, and managing supplier relationships in one place.",
      },
      {
        question:
          "How is Procman.ai different from traditional procurement?",
        answer:
          "Traditional procurement often relies on emails, spreadsheets, and manual follow-ups. Procman.ai centralizes the entire process, making it easier to collaborate with suppliers, compare quotations, maintain records, and gain better visibility into every procurement activity.",
      },
      {
        question:
          "Can I use Procman.ai with my existing suppliers?",
        answer:
          "es. You can invite your existing suppliers to participate in RFQs while also discovering new suppliers through the platform, giving you greater flexibility and more competitive sourcing options.",
      },
      {
        question:
          "Which businesses can benefit from Procman.ai?",
        answer:
          "Procman.ai is suitable for businesses of all sizes across industries such as manufacturing, healthcare, construction, education, retail, hospitality, logistics, infrastructure, and professional services that want to improve procurement efficiency.",
      },
      {
        question: "Is my procurement data secure?",
        answer:
          "Yes. Protecting business data is a priority. Procman.ai is designed with security measures to help safeguard procurement information, supplier data, and business documents while providing controlled access to authorized users.",
      },
      {
        question: "How do I get started with Procman.ai?",
        answer:
          "Getting started is simple. Create your business account, set up your company profile, invite suppliers or discover new ones, and begin creating RFQs to manage your procurement process more efficiently.",
      },
    ],
  },

  cta: {
    section_title: "Ready to Transform Your Procurement?",
    section_description:
      "Reduce sourcing time ,compare quotations instantly and manage procurement from RFQ to payment",
    primary_cta: {
      label: "Start Buying ",
      url: "/create-rfq",
    },
    secondary_cta: {
      label: "Start Selling",
      url: "/vendor/register",
    },
    benefits: [
      { text: " No setup cost" },
      { text: "Get started in minutes" },
      { text: "Trusted by growing businesses" },
    ],
  },

  seo: {
    meta_title: "AI procurement",
    meta_description: "meta desc",
    meta_keywords: "procurement, rfq, suppliers",
    canonical_url: "https://procman.ai",
    og_title: "",
    og_description: "",
    twitter_title: "",
    twitter_description: "",
    robots_index: true,
    seo_content: "",
  },

  schema: {
    enable_faq: true,
    enable_webpage: true,
    custom_json: "",
    additional_schema_type: "",
    additional_schema_data: {},
  },

  hero_background_image: null,
  hero_desktop_dashboard_image: null,
  hero_mobile_dashboard_image: null,
  buyer_card_image: null,
  supplier_card_image: null,
  security_feature_1_image: null,
  security_feature_2_image: null,
  security_feature_3_image: null,
  og_image: null,
  twitter_image: null,
};

const OBJECT_FIELDS = [
  "hero",
  "trusted_brands",
  "working",
  "buyer_supplier",
  "platform_features",
  "comparision",
  "enterprise_security",
  "industries",
  "case_studies",
  "testimonials",
  "faq",
  "cta",
  "seo",
];

const IMAGE_FIELDS = [
  "hero_background_image",
  "hero_desktop_dashboard_image",
  "hero_mobile_dashboard_image",
  "buyer_card_image",
  "supplier_card_image",
  "security_feature_1_image",
  "security_feature_2_image",
  "security_feature_3_image",
  "og_image",
  "twitter_image",
];
const CONTENT_FIELDS = [...OBJECT_FIELDS, "schema"];

const merge = (data = {}) => ({
  ...defaults,
  ...data,
  ...Object.fromEntries(
    OBJECT_FIELDS.map((key) => [key, { ...defaults[key], ...data[key] }]),
  ),
  hero: {
    ...defaults.hero,
    ...data.hero,
    buyer_card: { ...defaults.hero.buyer_card, ...data.hero?.buyer_card, cta: { ...defaults.hero.buyer_card.cta, ...data.hero?.buyer_card?.cta } },
    supplier_card: { ...defaults.hero.supplier_card, ...data.hero?.supplier_card, cta: { ...defaults.hero.supplier_card.cta, ...data.hero?.supplier_card?.cta } },
    actions: Array.isArray(data.hero?.actions) ? data.hero.actions : [],
  },
  buyer_supplier: {
    ...defaults.buyer_supplier,
    ...data.buyer_supplier,
    buyer_card: { ...defaults.buyer_supplier.buyer_card, ...data.buyer_supplier?.buyer_card, cta: { ...defaults.buyer_supplier.buyer_card.cta, ...data.buyer_supplier?.buyer_card?.cta }, features: Array.isArray(data.buyer_supplier?.buyer_card?.features) ? data.buyer_supplier.buyer_card.features : [] },
    supplier_card: { ...defaults.buyer_supplier.supplier_card, ...data.buyer_supplier?.supplier_card, cta: { ...defaults.buyer_supplier.supplier_card.cta, ...data.buyer_supplier?.supplier_card?.cta }, features: Array.isArray(data.buyer_supplier?.supplier_card?.features) ? data.buyer_supplier.supplier_card.features : [] },
  },
  comparision: {
    ...defaults.comparision,
    ...data.comparision,
    procman: { ...defaults.comparision.procman, ...data.comparision?.procman, points: Array.isArray(data.comparision?.procman?.points) ? data.comparision.procman.points : [] },
    traditional: { ...defaults.comparision.traditional, ...data.comparision?.traditional, points: Array.isArray(data.comparision?.traditional?.points) ? data.comparision.traditional.points : [] },
  },
  cta: {
    ...defaults.cta,
    ...data.cta,
    primary_cta: { ...defaults.cta.primary_cta, ...data.cta?.primary_cta },
    secondary_cta: { ...defaults.cta.secondary_cta, ...data.cta?.secondary_cta },
    benefits: Array.isArray(data.cta?.benefits) ? data.cta.benefits : [],
  },
  faq: { ...defaults.faq, ...data.faq, faqs: Array.isArray(data.faq?.faqs) ? data.faq.faqs : [] },
  seo: {
    ...defaults.seo, ...data.seo,
    og_title: data.seo?.og_title || data.seo?.meta_title || "",
    og_description: data.seo?.og_description || data.seo?.meta_description || "",
    twitter_title: data.seo?.twitter_title || data.seo?.og_title || data.seo?.meta_title || "",
    twitter_description: data.seo?.twitter_description || data.seo?.og_description || data.seo?.meta_description || "",
  },
  schema: {
    ...defaults.schema,
    ...(data.schema && typeof data.schema === "object" ? data.schema : {}),
    additional_schema_data:
      data.schema?.additional_schema_data && typeof data.schema.additional_schema_data === "object"
        ? data.schema.additional_schema_data
        : {},
  },
  ...Object.fromEntries(IMAGE_FIELDS.map((key) => [key, data[key] ?? null])),
});

const input = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 14px",
  border: "1.5px solid #cbd5e1",
  borderRadius: 8,
  marginTop: 6,
  fontSize: 14,
  fontWeight: 400,
  color: "#0f172a",
  backgroundColor: "#ffffff",
  transition: "all 0.2s ease-in-out",
};

const formatFieldLabel = (key) =>
  key
    .split("_")
    .map((word) => {
      const acronyms = { cta: "CTA", url: "URL", seo: "SEO", id: "ID", json: "JSON", ld: "LD" };
      return acronyms[word] || `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
    })
    .join(" ");

const setErrorEntry = (setErrors, key, message) =>
  setErrors((prev) => {
    if (prev[key] === message) return prev;
    const next = { ...prev };
    if (message) next[key] = message;
    else delete next[key];
    return next;
  });

const Section = ({ title, description, children }) => (
  <section
    style={{
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: 14,
      padding: "26px 28px",
      marginBottom: 28,
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03), 0 2px 4px -2px rgba(0,0,0,0.03)",
    }}
  >
    <div style={{ borderBottom: "2px solid #f1f5f9", pb: 12, paddingBottom: 12, marginBottom: 20 }}>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.01em" }}>{title}</h2>
      {description && (
        <p style={{ margin: "6px 0 0", fontSize: 13, color: "#64748b", lineHeight: 1.6, fontWeight: 400 }}>
          {description}
        </p>
      )}
    </div>
    <div style={{ display: "grid", gap: 12 }}>{children}</div>
  </section>
);

const SubBlock = ({ title, description, headerAction, children }) => (
  <div
    className="subblock"
  // style={{
  //   paddingTop: 10,
  //   marginTop: 10,
  //   borderTop: "1px solid #eef2f7",
  // }}
  >
    {(title || headerAction) && (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
        {title && (
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#334155",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              background: "#f1f5f9",
              padding: "4px 10px",
              borderRadius: 6,
              border: "1px solid #e2e8f0",
              display: "inline-block",
            }}
          >
            {title}
          </div>
        )}
        {headerAction && <div>{headerAction}</div>}
      </div>
    )}
    {description && (
      <p style={{ margin: "-4px 0 14px", fontSize: 13, color: "#64748b", lineHeight: 1.5 }}>
        {description}
      </p>
    )}
    {children}
  </div>
);

const Field = ({ label, value, onChange, textarea, type = "text", readOnly = false, limits, error }) => {
  const len = (value || "").toString().trim().length;
  const showCount = Boolean(limits?.max);
  const invalid = Boolean(error);
  const fieldStyle = {
    ...input,
    ...(invalid ? { borderColor: "#e11d48", background: "#fff1f2" } : {}),
  };
  return (
    <label style={{ display: "block", marginBottom: 16, fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <span>
          {label}
          {limits?.required && <span style={{ color: "#e11d48" }}> *</span>}
        </span>
        {showCount && (
          <span style={{ fontSize: 11, fontWeight: 600, color: invalid ? "#e11d48" : "#64748b", whiteSpace: "nowrap" }}>
            {len}/{limits.max}
          </span>
        )}
      </div>
      {textarea ? (
        <textarea rows={3} style={fieldStyle} value={value || ""} readOnly={readOnly} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} style={fieldStyle} value={value || ""} readOnly={readOnly} onChange={(e) => onChange(e.target.value)} />
      )}
      {limits?.help && !error && (
        <div style={{ marginTop: 5, fontSize: 12, color: "#64748b", fontWeight: 400, lineHeight: 1.4 }}>{limits.help}</div>
      )}
      {error && <div style={{ marginTop: 5, fontSize: 12, color: "#e11d48", fontWeight: 600 }}>{error}</div>}
    </label>
  );
};

function Repeater({ title, description, items, fields, fieldRules = {}, repeaterRules, path, errors, setErrors, onChange, beforeItems }) {
  const countError = repeaterRules ? validateRepeaterCount(items, repeaterRules) : null;

  useEffect(() => {
    setErrorEntry(setErrors, path, countError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countError, path]);

  const atMax = Boolean(repeaterRules?.max && items.length >= repeaterRules.max);
  const atMin = Boolean(repeaterRules?.min && items.length <= repeaterRules.min);

  const add = () => {
    if (atMax) return;
    onChange([...items, Object.fromEntries(fields.map(([key]) => [key, ""]))]);
  };
  const remove = (index) => {
    if (atMin) return;
    onChange(items.filter((_, i) => i !== index));
  };
  const setFieldValue = (index, key, value) => {
    onChange(items.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
    const rules = fieldRules[key];
    const message = validateByRules(value, rules, formatFieldLabel(key));
    setErrorEntry(setErrors, `${path}[${index}].${key}`, message);
  };

  const inlineFields = fields.filter(([, textarea]) => !textarea);
  const blockFields = fields.filter(([, textarea]) => textarea);

  const nounLabel = repeaterRules?.noun || "item";

  const addBtn = (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {repeaterRules && (
        <span style={{ fontSize: 12, fontWeight: 600, color: countError ? "#e11d48" : "#64748b" }}>
          {items.length}{repeaterRules.max ? `/${repeaterRules.max}` : ""} {nounLabel}s
          {repeaterRules.min ? ` · min ${repeaterRules.min}` : ""}
        </span>
      )}
      <button
        type="button"
        className="primary"
        onClick={add}
        disabled={atMax}
        style={{
          opacity: atMax ? 0.5 : 1,
          cursor: atMax ? "not-allowed" : "pointer",
          margin: 0,
        }}
      >
        + Add {nounLabel}
      </button>
    </div>
  );

  return (
    <SubBlock title={title} description={description} headerAction={addBtn}>
      {beforeItems}
      {countError && <div style={{ marginBottom: 12, fontSize: 12, color: "#e11d48", fontWeight: 600 }}>{countError}</div>}
      {repeaterRules?.help && <div style={{ marginBottom: 12, fontSize: 12, color: "#64748b" }}>{repeaterRules.help}</div>}

      <div style={{ display: "grid", gap: 12 }}>
        {items.map((item, index) => (
          <div
            key={index}
            style={{
              border: "1px solid #cbd5e1",
              borderRadius: 10,
              background: "#ffffff",
              padding: "16px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "#e0e7ff",
                  color: "#4f46e5",
                  fontSize: 13,
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {index + 1}
              </span>
              <button
                type="button"
                onClick={() => remove(index)}
                disabled={atMin}
                style={{
                  margin: 0,
                  borderColor: "#fecaca",
                  background: "#fff1f2",
                  color: "#e11d48",
                  opacity: atMin ? 0.4 : 1,
                  cursor: atMin ? "not-allowed" : "pointer",
                }}
              >
                Remove
              </button>
            </div>
            {inlineFields.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${inlineFields.length}, minmax(140px, 1fr))`, gap: 12 }}>
                {inlineFields.map(([key]) => (
                  <Field
                    key={key}
                    label={formatFieldLabel(key)}
                    value={item[key]}
                    limits={fieldRules[key]}
                    error={errors?.[`${path}[${index}].${key}`]}
                    onChange={(value) => setFieldValue(index, key, value)}
                  />
                ))}
              </div>
            )}
            {blockFields.map(([key]) => (
              <Field
                key={key}
                label={formatFieldLabel(key)}
                textarea
                value={item[key]}
                limits={fieldRules[key]}
                error={errors?.[`${path}[${index}].${key}`]}
                onChange={(value) => setFieldValue(index, key, value)}
              />
            ))}
          </div>
        ))}
      </div>
      {!items.length && (
        <p style={{ margin: "10px 0 0", fontSize: 13, color: "#64748b", italic: "true" }}>Nothing added yet.</p>
      )}
    </SubBlock>
  );
}

function StringListEditor({ title, description, items, path, rules, repeaterRules, errors, setErrors, onChange, placeholder = "Text" }) {
  const countError = repeaterRules ? validateRepeaterCount(items, repeaterRules) : null;

  useEffect(() => {
    setErrorEntry(setErrors, path, countError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countError, path]);

  const atMax = Boolean(repeaterRules?.max && items.length >= repeaterRules.max);
  const atMin = Boolean(repeaterRules?.min && items.length <= repeaterRules.min);

  const add = () => {
    if (!atMax) onChange([...items, ""]);
  };
  const remove = (index) => {
    if (!atMin) onChange(items.filter((_, i) => i !== index));
  };
  const setValue = (index, value) => {
    onChange(items.map((row, i) => (i === index ? value : row)));
    setErrorEntry(setErrors, `${path}[${index}]`, validateText(value, rules, title));
  };

  const nounLabel = repeaterRules?.noun || "item";

  const addBtn = (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {repeaterRules && (
        <span style={{ fontSize: 12, fontWeight: 600, color: countError ? "#e11d48" : "#64748b" }}>
          {items.length}{repeaterRules.max ? `/${repeaterRules.max}` : ""} {nounLabel}s
        </span>
      )}
      <button
        type="button"
        className="primary"
        onClick={add}
        disabled={atMax}
        style={{ opacity: atMax ? 0.5 : 1, cursor: atMax ? "not-allowed" : "pointer", margin: 0 }}
      >
        + Add {nounLabel}
      </button>
    </div>
  );

  return (
    <SubBlock title={title} description={description} headerAction={addBtn}>
      {countError && <div style={{ marginBottom: 12, fontSize: 12, color: "#e11d48", fontWeight: 600 }}>{countError}</div>}
      {items.map((item, index) => {
        const error = errors?.[`${path}[${index}]`];
        return (
          <div key={index} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input
                style={{ ...input, marginTop: 0, ...(error ? { borderColor: "#e11d48", background: "#fff1f2" } : {}) }}
                value={item}
                placeholder={placeholder}
                onChange={(e) => setValue(index, e.target.value)}
              />
              <button
                type="button"
                onClick={() => remove(index)}
                disabled={atMin}
                style={{ opacity: atMin ? 0.4 : 1, borderColor: "#fecaca", background: "#fff1f2", color: "#e11d48", height: "42px", margin: 0 }}
              >
                Remove
              </button>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              {error ? <span style={{ fontSize: 12, color: "#e11d48", fontWeight: 600 }}>{error}</span> : <span />}
              {rules?.max && <span style={{ fontSize: 11, color: "#64748b" }}>{(item || "").length}/{rules.max}</span>}
            </div>
          </div>
        );
      })}
      {!items.length && (
        <p style={{ margin: "10px 0 0", fontSize: 13, color: "#64748b" }}>Nothing added yet.</p>
      )}
    </SubBlock>
  );
}

function CtaFields({ label, value, onChange, pathPrefix, labelRules, errors, setErrors }) {
  const labelKey = `${pathPrefix}.label`;
  const urlKey = `${pathPrefix}.url`;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 8, background: "#f8fafc", padding: "12px 14px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
      <Field
        label={`${label} Label`}
        value={value?.label}
        limits={labelRules}
        error={errors?.[labelKey]}
        onChange={(v) => {
          onChange({ ...value, label: v });
          setErrorEntry(setErrors, labelKey, validateText(v, labelRules, `${label} Label`));
        }}
      />
      <Field
        label={`${label} URL`}
        value={value?.url}
        error={errors?.[urlKey]}
        onChange={(v) => {
          onChange({ ...value, url: v });
          setErrorEntry(setErrors, urlKey, validateUrl(v, true, `${label} URL`));
        }}
      />
    </div>
  );
}

function IconCardEditor({ title, value, onChange, path, errors, setErrors }) {
  const items = value.items || [];
  const titleRules = TEXT_RULES[`${path}.title`];
  const ctaLabelRules = TEXT_RULES[`${path}.cta.label`];
  const itemRules = TEXT_RULES[`${path}.item.label`];
  const repeaterRules = REPEATER_RULES[`${path}.items`];
  const countError = repeaterRules ? validateRepeaterCount(items, repeaterRules) : null;

  useEffect(() => {
    setErrorEntry(setErrors, `${path}.items`, countError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countError, path]);

  const atMax = Boolean(repeaterRules?.max && items.length >= repeaterRules.max);
  const atMin = Boolean(repeaterRules?.min && items.length <= repeaterRules.min);

  const addBtn = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: countError ? "#e11d48" : "#64748b" }}>
        {items.length}{repeaterRules?.max ? `/${repeaterRules.max}` : ""}
      </span>
      <button
        type="button"
        className="primary"
        style={{ opacity: atMax ? 0.5 : 1, margin: 0, cursor: atMax ? "not-allowed" : "pointer" }}
        disabled={atMax}
        onClick={() => !atMax && onChange({ ...value, items: [...items, { label: "" }] })}
      >
        + Add item
      </button>
    </div>
  );

  return (
    <SubBlock title={title} headerAction={addBtn}>
      <Field
        label="Card Title"
        value={value.title}
        limits={titleRules}
        error={errors?.[`${path}.title`]}
        onChange={(v) => {
          onChange({ ...value, title: v });
          setErrorEntry(setErrors, `${path}.title`, validateText(v, titleRules, "Card Title"));
        }}
      />
      <CtaFields
        label="CTA"
        value={value.cta}
        pathPrefix={`${path}.cta`}
        labelRules={ctaLabelRules}
        errors={errors}
        setErrors={setErrors}
        onChange={(cta) => onChange({ ...value, cta })}
      />
      {countError && <div style={{ marginBottom: 8, fontSize: 12, color: "#e11d48", fontWeight: 600 }}>{countError}</div>}
      <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
        {items.map((item, index) => {
          const errorKey = `${path}.items[${index}].label`;
          return (
            <div key={index} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "end", background: "#f8fafc", padding: "10px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
              <Field
                label={`Item-${index + 1} Label`}
                value={item.label}
                limits={itemRules}
                error={errors?.[errorKey]}
                onChange={(v) => {
                  onChange({ ...value, items: items.map((row, i) => (i === index ? { ...row, label: v } : row)) });
                  setErrorEntry(setErrors, errorKey, validateText(v, itemRules, "Item Label"));
                }}
              />
              <button
                type="button"
                disabled={atMin}
                onClick={() => !atMin && onChange({ ...value, items: items.filter((_, i) => i !== index) })}
                style={{ marginBottom: 16, borderColor: "#fecaca", background: "#fff1f2", color: "#e11d48", opacity: atMin ? 0.4 : 1, height: "42px" }}
              >
                Remove
              </button>
            </div>
          );
        })}
      </div>
    </SubBlock>
  );
}

function FeatureCardEditor({ title, value, onChange, path, errors, setErrors }) {
  const features = value.features || [];
  const titleRules = TEXT_RULES[`${path}.title`];
  const ctaLabelRules = TEXT_RULES[`${path}.cta.label`];
  const featureRules = TEXT_RULES[`${path}.feature`];
  const repeaterRules = REPEATER_RULES[`${path}.features`];
  const countError = repeaterRules ? validateRepeaterCount(features, repeaterRules) : null;

  useEffect(() => {
    setErrorEntry(setErrors, `${path}.features`, countError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countError, path]);

  const atMax = Boolean(repeaterRules?.max && features.length >= repeaterRules.max);
  const atMin = Boolean(repeaterRules?.min && features.length <= repeaterRules.min);

  const addBtn = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: countError ? "#e11d48" : "#64748b" }}>
        {features.length}{repeaterRules?.max ? `/${repeaterRules.max}` : ""}
      </span>
      <button
        type="button"
        className="primary"
        style={{ opacity: atMax ? 0.5 : 1, margin: 0, cursor: atMax ? "not-allowed" : "pointer" }}
        disabled={atMax}
        onClick={() => !atMax && onChange({ ...value, features: [...features, ""] })}
      >
        + Add feature
      </button>
    </div>
  );

  return (
    <SubBlock title={title} headerAction={addBtn}>
      <Field
        label="Card Title"
        value={value.title}
        limits={titleRules}
        error={errors?.[`${path}.title`]}
        onChange={(v) => {
          onChange({ ...value, title: v });
          setErrorEntry(setErrors, `${path}.title`, validateText(v, titleRules, "Card Title"));
        }}
      />
      <CtaFields
        label="CTA"
        value={value.cta}
        pathPrefix={`${path}.cta`}
        labelRules={ctaLabelRules}
        errors={errors}
        setErrors={setErrors}
        onChange={(cta) => onChange({ ...value, cta })}
      />
      {countError && <div style={{ marginBottom: 8, fontSize: 12, color: "#e11d48", fontWeight: 600 }}>{countError}</div>}
      <div style={{ marginTop: 12 }}>
        {features.map((feature, index) => {
          const errorKey = `${path}.features[${index}]`;
          const error = errors?.[errorKey];
          return (
            <div key={index} style={{ marginBottom: 12, background: "#f8fafc", padding: "10px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <input
                  style={{ ...input, marginTop: 0, ...(error ? { borderColor: "#e11d48", background: "#fff1f2" } : {}) }}
                  value={feature}
                  onChange={(e) => {
                    const v = e.target.value;
                    onChange({ ...value, features: features.map((row, i) => (i === index ? v : row)) });
                    setErrorEntry(setErrors, errorKey, validateText(v, featureRules, "Feature"));
                  }}
                />
                <button
                  type="button"
                  disabled={atMin}
                  onClick={() => !atMin && onChange({ ...value, features: features.filter((_, i) => i !== index) })}
                  style={{ opacity: atMin ? 0.4 : 1, borderColor: "#fecaca", background: "#fff1f2", color: "#e11d48", height: "42px", margin: 0 }}
                >
                  Remove
                </button>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                {error ? <span style={{ fontSize: 12, color: "#e11d48", fontWeight: 600 }}>{error}</span> : <span />}
                {featureRules?.max && <span style={{ fontSize: 11, color: "#64748b" }}>{(feature || "").length}/{featureRules.max}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </SubBlock>
  );
}

function ImageUploadField({ label, fieldName, value, defaultValue, defaultNote, disabled, uploading, onUpload, onPreview, error, setError }) {
  const [localPreview, setLocalPreview] = useState(null);
  const spec = IMAGE_SPECS[fieldName];
  const isCustom = Boolean(localPreview || value);
  const previewUrl = localPreview || value || defaultValue;

  const handleFile = async (file) => {
    if (!file) return;
    if (spec) {
      const problems = await validateImageBeforeUpload(file, fieldName);
      if (problems.length) {
        setError?.(problems.join(" "));
        return;
      }
    }
    setError?.(null);
    setLocalPreview(URL.createObjectURL(file));
    onUpload(fieldName, file);
  };

  return (
    <div style={{ marginBottom: 18, background: "#f8fafc", padding: "14px", borderRadius: 10, border: "1px solid #e2e8f0" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 8 }}>{label}</div>
      <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 16, alignItems: "center" }}>
        <div
          style={{
            height: 80,
            width: 130,
            borderRadius: 8,
            border: `1.5px dashed ${error ? "#fca5a5" : "#cbd5e1"}`,
            background: "#ffffff",
            overflow: "hidden",
            display: "grid",
            placeItems: "center",
          }}
        >
          {previewUrl ? (
            <button
              type="button"
              aria-label={`Preview ${label}`}
              onClick={() => onPreview && onPreview(previewUrl)}
              style={{ padding: 0, margin: 0, border: 0, background: "none", width: "100%", height: "100%", cursor: "zoom-in" }}
            >
              <img src={previewUrl} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </button>
          ) : (
            <span style={{ color: "#64748b", fontSize: 11, textAlign: "center", padding: 6, fontWeight: 500 }}>No image</span>
          )}
        </div>
        <div>
          <input
            id={`img-${fieldName}`}
            type="file"
            accept={spec ? spec.formats.join(",") : "image/png,image/jpeg,image/webp"}
            disabled={disabled || uploading}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              handleFile(file);
            }}
            style={{ display: "none" }}
          />
          <label
            htmlFor={`img-${fieldName}`}
            style={{
              display: "inline-block",
              border: "1px solid #bfdbfe",
              borderRadius: 6,
              padding: "8px 14px",
              background: "#eff6ff",
              color: "#2563eb",
              fontSize: 13,
              fontWeight: 700,
              cursor: disabled || uploading ? "not-allowed" : "pointer",
              opacity: disabled || uploading ? 0.55 : 1,
              transition: "all 0.2s ease",
            }}
          >
            {uploading ? "Uploading…" : isCustom ? "Replace image" : "Upload image"}
          </label>
          {spec?.help && !error && (
            <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: 12, lineHeight: 1.4 }}>{spec.help}</p>
          )}
          {!isCustom && previewUrl && defaultNote && !error && (
            <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: 12, lineHeight: 1.4 }}>{defaultNote}</p>
          )}
          {disabled && (
            <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: 12, lineHeight: 1.4 }}>
              Save the homepage once before uploading images.
            </p>
          )}
          {error && <p style={{ margin: "8px 0 0", color: "#e11d48", fontSize: 12, fontWeight: 600 }}>{error}</p>}
        </div>
      </div>
    </div>
  );
}

const ADDITIONAL_SCHEMA_FIELDS = {
  Organization: [
    ["name", "Organization name"],
    ["url", "Organization URL"],
    ["description", "Organization description", true],
    ["logo", "Logo URL"],
  ],
  Service: [
    ["name", "Service name"],
    ["description", "Service description", true],
    ["url", "Service URL"],
    ["provider_name", "Provider name"],
  ],
  SoftwareApplication: [
    ["name", "Application name"],
    ["description", "Application description", true],
    ["url", "Application URL"],
    ["application_category", "Application category"],
    ["operating_system", "Operating system"],
  ],
};

const buildAdditionalSchema = (type, values = {}) => {
  const value = (key, fallback = "") => values[key]?.trim() || fallback;
  switch (type) {
    case "Organization":
      return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: value("name"),
        url: value("url"),
        description: value("description"),
        logo: value("logo"),
      };
    case "Service":
      return {
        "@context": "https://schema.org",
        "@type": "Service",
        name: value("name"),
        description: value("description"),
        url: value("url"),
        provider: {
          "@type": "Organization",
          name: value("provider_name", "Procman"),
        },
      };
    case "SoftwareApplication":
      return {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: value("name"),
        description: value("description"),
        url: value("url"),
        applicationCategory: value("application_category", "BusinessApplication"),
        operatingSystem: value("operating_system", "Web"),
      };
    default:
      return null;
  }
};

function AdditionalSchemaGenerator({ schema, onChange }) {
  const [preview, setPreview] = useState(null);
  const selectedType = schema.additional_schema_type || "";
  const values = schema.additional_schema_data || {};
  const fields = ADDITIONAL_SCHEMA_FIELDS[selectedType] || [];
  const updateValue = (key, value) =>
    onChange({
      ...schema,
      additional_schema_data: { ...values, [key]: value },
    });

  return (
    <SubBlock title="Generate an additional schema">
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#1e293b", marginBottom: 12 }}>
        Schema type
        <select
          style={input}
          value={selectedType}
          onChange={(e) => {
            onChange({
              ...schema,
              additional_schema_type: e.target.value,
              additional_schema_data: {},
            });
            setPreview(null);
          }}
        >
          <option value="">None</option>
          <option value="Organization">Organization</option>
          <option value="Service">Service</option>
          <option value="SoftwareApplication">Software Application</option>
        </select>
      </label>
      {fields.map(([key, label, textarea]) => (
        <Field
          key={key}
          label={label}
          value={values[key]}
          textarea={textarea}
          onChange={(value) => updateValue(key, value)}
        />
      ))}
      {selectedType && (
        <>
          <button
            type="button"
            className="primary"
            onClick={() => setPreview(buildAdditionalSchema(selectedType, values))}
            style={{ marginTop: 8 }}
          >
            Generate schema
          </button>
          {preview && (
            <pre
              style={{
                margin: "14px 0 0",
                padding: 14,
                borderRadius: 8,
                background: "#0f172a",
                color: "#e2e8f0",
                fontSize: 12,
                lineHeight: 1.5,
                overflowX: "auto",
              }}
            >
              {JSON.stringify(preview, null, 2)}
            </pre>
          )}
        </>
      )}
    </SubBlock>
  );
}

export default function HomePageMaster() {
  const [record, setRecord] = useState(null);
  const [content, setContent] = useState(defaults);
  const [notice, setNotice] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState({});
  const [overlayUrl, setOverlayUrl] = useState("");
  const [errors, setErrors] = useState({});
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    axiosInstance
      .get("/api/superadmin/home-page-master/")
      .then(({ data }) => {
        const row = data?.data?.[0];
        if (row) {
          setRecord(row);
          setContent(merge(row));
        }
      })
      .catch(() => setNotice("Could not load Homepage Master."))
      .finally(() => setLoaded(true));
  }, []);

  const setGroup = (group, key, value) =>
    setContent((state) => {
      const nextGroup = { ...state[group], [key]: value };

      // Meta title/description change OG + Twitter auto-sync
      if (group === "seo" && key === "meta_title") {
        nextGroup.og_title = value;
        nextGroup.twitter_title = value;
      }
      if (group === "seo" && key === "meta_description") {
        nextGroup.og_description = value;
        nextGroup.twitter_description = value;
      }

      return { ...state, [group]: nextGroup };
    });
  const setField = (group, value) => setContent((state) => ({ ...state, [group]: value }));

  const setValidatedGroup = (group, key, value, label) => {
    setGroup(group, key, value);
    setErrorEntry(setErrors, `${group}.${key}`, validateText(value, TEXT_RULES[`${group}.${key}`], label));
  };

  const hasBlockingErrors = Object.keys(errors).length > 0;

  const refresh = async (id) => {
    const targetId = id || record?.id;
    if (!targetId) return;
    const { data } = await axiosInstance.get(`/api/superadmin/home-page-master/${targetId}/`);
    if (data?.data) {
      setRecord(data.data);
      setContent(merge(data.data));
    }
  };

  const save = async () => {
    if (hasBlockingErrors) {
      setNotice(`Fix ${Object.keys(errors).length} validation error(s) before saving.`);
      return;
    }
    const payload = Object.fromEntries(CONTENT_FIELDS.map((field) => [field, content[field]]));
    setSaving(true);
    try {
      if (record) {
        await axiosInstance.put(`/api/superadmin/home-page-master/${record.id}/`, payload);
        await refresh(record.id);
      } else {
        const response = await axiosInstance.post("/api/superadmin/home-page-master/", payload);
        let targetId = response.data?.id;
        if (response.data?.success === false) {
          const { data } = await axiosInstance.get("/api/superadmin/home-page-master/");
          const existing = data?.data?.[0];
          if (existing) {
            targetId = existing.id;
            await axiosInstance.put(`/api/superadmin/home-page-master/${targetId}/`, payload);
          }
        }
        if (targetId) await refresh(targetId);
      }
      setNotice("Homepage saved successfully.");
    } catch (error) {
      const errorsData = error?.response?.data;
      setNotice(errorsData?.message || errorsData?.detail || (errorsData ? Object.values(errorsData).flat().join(" ") : "Could not save Homepage."));
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (fieldName, file) => {
    if (!record) {
      setNotice("Save the homepage once before uploading images.");
      return;
    }
    setUploadingImages((state) => ({ ...state, [fieldName]: true }));
    try {
      const body = new FormData();
      body.append(fieldName, file);
      await axiosInstance.put(`/api/superadmin/home-page-master/${record.id}/`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh(record.id);
      setNotice(`${formatFieldLabel(fieldName)} uploaded successfully.`);
    } catch (error) {
      setNotice(error?.response?.data?.message || error?.response?.data?.detail || `Could not upload ${formatFieldLabel(fieldName)}.`);
    } finally {
      setUploadingImages((state) => ({ ...state, [fieldName]: false }));
    }
  };

  const imageProps = (fieldName) => ({
    fieldName,
    value: content[fieldName],
    disabled: !record,
    uploading: Boolean(uploadingImages[fieldName]),
    onUpload: uploadImage,
    onPreview: setOverlayUrl,
    error: imageErrors[fieldName],
    setError: (message) => setErrorEntry(setImageErrors, fieldName, message),
  });

  return (
    <div className="homepage-cms" style={{ minHeight: "100vh", padding: "32px 24px", background: "#f8fafc", fontFamily: "Inter,Arial,sans-serif" }}>
      <style>{`
        .homepage-cms button:not(.rte-wrapper *) {
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          background: #eff6ff;
          color: #2563eb;
          padding: 8px 14px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          margin-right: 8px;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .homepage-cms button:not(.rte-wrapper button):hover {
          background: #dbeafe;
          border-color: #93c5fd;
        }
        .homepage-cms .primary {
          border: 0;
          background: #2563eb;
          color: #ffffff;
          padding: 9px 18px;
          font-size: 13px;
          font-weight: 600;
          box-shadow: 0 1px 2px rgba(37,99,235,0.2);
        }
        .homepage-cms .primary:hover {
          background: #1d4ed8;
        }
        .homepage-cms .danger {
          border: 1px solid #fecaca;
          background: #fff1f2;
          color: #e11d48;
        }
        .homepage-cms .danger:hover {
          background: #ffe4e6;
        }
        .homepage-cms input:not(:where(.rte-wrapper *)):focus, .homepage-cms textarea:not(:where(.rte-wrapper *)):focus, .homepage-cms select:not(:where(.rte-wrapper *)):focus {
          outline: none;
          border-color: #2563eb !important;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.15) !important;
        }
        .homepage-cms .subblock:first-child {
          border-top: 0;
          margin-top: 0;
          padding-top: 0;
        }
      `}</style>

      {!loaded ? (
        <div style={{ padding: "60px 0", textAlign: "center", color: "#64748b", fontSize: 14, fontWeight: 600 }}>
          Loading homepage content...
        </div>
      ) : (
        <>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, marginBottom: 28, background: "#ffffff", padding: "20px 24px", borderRadius: 12, border: "1px solid #e2e8f0" }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>Homepage</h1>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748b" }}>
                Manage the live Procman AI homepage content, images, SEO, and structured data.
              </p>
            </div>
            <div style={{ position: "fixed", top: 88, right: 48, zIndex: 1200, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
              <button
                className="primary"
                disabled={saving || hasBlockingErrors}
                onClick={save}
                title={hasBlockingErrors ? `${Object.keys(errors).length} field(s) need fixing before you can save` : undefined}
                style={{
                  background: "#1d4ed8",
                  color: "white",
                  margin: 0,
                  boxShadow: "0 6px 16px rgba(37,99,235,.3)",
                  opacity: hasBlockingErrors ? 0.5 : 1,
                  cursor: hasBlockingErrors ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!hasBlockingErrors) {
                    e.currentTarget.style.background = "#1e40af";
                    e.currentTarget.style.boxShadow = "0 8px 20px rgba(37,99,235,.4)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#1d4ed8";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(37,99,235,.3)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >

                {saving ? "Saving..." : "Save Data"}
              </button>
              {hasBlockingErrors && (
                <span style={{ fontSize: 11, fontWeight: 700, color: "#e11d48", background: "#fff1f2", border: "1px solid #fecaca", borderRadius: 6, padding: "3px 8px" }}>
                  {Object.keys(errors).length} field(s) need fixing
                </span>
              )}
            </div>
          </div>

          {notice && (
            <div
              style={{
                padding: "12px 16px",
                marginBottom: 20,
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                background: notice.toLowerCase().includes("success") ? "#f0fdf4" : "#fef2f2",
                border: `1px solid ${notice.toLowerCase().includes("success") ? "#bbf7d0" : "#fecaca"}`,
                color: notice.toLowerCase().includes("success") ? "#166534" : "#b91c1c",
              }}
            >
              {notice}
            </div>
          )}



          {/* SEO */}
          <Section title="SEO">
            <SubBlock title="">
              <Field label="Meta Title" value={content.seo.meta_title} onChange={(v) => setGroup("seo", "meta_title", v)} />
              <Field label="Meta Description" textarea value={content.seo.meta_description} onChange={(v) => setGroup("seo", "meta_description", v)} />
              <Field label="Meta Keywords" value={content.seo.meta_keywords} onChange={(v) => setGroup("seo", "meta_keywords", v)} />
              <Field label="Canonical URL" value={content.seo.canonical_url} onChange={(v) => setGroup("seo", "canonical_url", v)} />
              <Field label="OG Title" value={content.seo.og_title} onChange={(v) => setGroup("seo", "og_title", v)} />
              <Field label="OG Description" textarea value={content.seo.og_description} onChange={(v) => setGroup("seo", "og_description", v)} />
              <Field label="Twitter Title" value={content.seo.twitter_title} onChange={(v) => setGroup("seo", "twitter_title", v)} />
              <Field label="Twitter Description" textarea value={content.seo.twitter_description} onChange={(v) => setGroup("seo", "twitter_description", v)} />
              <label style={{ display: "block", marginBottom: 16, fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                Robots Index
                <select
                  style={input}
                  value={content.seo.robots_index === false ? "noindex" : "index"}
                  onChange={(e) => setGroup("seo", "robots_index", e.target.value === "index")}
                >
                  <option value="index">index, follow</option>
                  <option value="noindex">noindex, nofollow</option>
                </select>
              </label>
            </SubBlock>
            <SubBlock
              title="SEO Content"
              description="Long-form SEO content with headings, lists, tables, links, and formatting."
            >
              <div className="rte-wrapper" style={{ marginTop: 12 }}>
                {/* <label
                  style={{
                    display: "block",
                    marginBottom: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#1e293b",
                  }}
                >
                  SEO Content
                </label> */}

                {/* <RichTextEditor
                  value={content.seo.seo_content || ""}
                  onChange={(html) =>
                    setGroup("seo", "seo_content", html)
                  }
                  placeholder="Write SEO content..."
                  minHeight={300}
                /> */}

                <Editor
                  value={content.seo.seo_content || ""}
                  onChange={(html) =>
                    setGroup("seo", "seo_content", html)
                  }
                  placeholder="Write SEO content..."
                  minHeight={300}
                />
              </div>
            </SubBlock>

            <SubBlock title="Images" description="Uploads immediately as soon as a file is chosen. Until you upload your own, OG falls back to the hero image and Twitter falls back to the OG image.">

              <ImageUploadField
                label="OG Image"
                {...imageProps("og_image")}
                defaultValue={content.hero_background_image}
                defaultNote="Using the hero background image by default"
              />
              <ImageUploadField
                label="Twitter Image"
                {...imageProps("twitter_image")}
                defaultValue={content.og_image || content.hero_background_image}
                defaultNote="Using the OG image by default"
              />
            </SubBlock>
          </Section>

          {/* SCHEMA */}
          <Section title="Schema" description="Structured data (JSON-LD) stored on the homepage record.">
            <SubBlock title="Custom JSON-LD">
              <Field
                label="Custom Schema JSON"
                textarea
                value={content.schema.custom_json}
                onChange={(v) => setGroup("schema", "custom_json", v)}
              />
            </SubBlock>
            <AdditionalSchemaGenerator
              schema={content.schema}
              onChange={(schema) => setField("schema", schema)}
            />
          </Section>



          {/* HERO */}
          <Section title="Hero Section">
            <SubBlock title="Content">
              <Field
                label="Section Badge"
                value={content.hero.section_badge}
                limits={TEXT_RULES["hero.section_badge"]}
                error={errors["hero.section_badge"]}
                onChange={(v) => setValidatedGroup("hero", "section_badge", v, "Section Badge")}
              />
              <Field
                label="Section Title"
                value={content.hero.section_title}
                limits={TEXT_RULES["hero.section_title"]}
                error={errors["hero.section_title"]}
                onChange={(v) => setValidatedGroup("hero", "section_title", v, "Section Title")}
              />
              <Field
                label="Section Description"
                textarea
                value={content.hero.section_description}
                limits={TEXT_RULES["hero.section_description"]}
                error={errors["hero.section_description"]}
                onChange={(v) => setValidatedGroup("hero", "section_description", v, "Section Description")}
              />
            </SubBlock>
            <Repeater
              title="Actions"
              description="The primary/secondary buttons shown in the hero (e.g. Browse Suppliers, Explore RFQs)."
              items={content.hero.actions || []}
              fields={[["label"], ["url"], ["variant"]]}
              fieldRules={{ label: TEXT_RULES["hero.action.label"], url: TEXT_RULES["hero.action.url"], variant: TEXT_RULES["hero.action.variant"] }}
              repeaterRules={REPEATER_RULES["hero.actions"]}
              path="hero.actions"
              errors={errors}
              setErrors={setErrors}
              onChange={(actions) => setGroup("hero", "actions", actions)}
            />
            <IconCardEditor title="Buyer Card" value={content.hero.buyer_card} path="hero.buyer_card" errors={errors} setErrors={setErrors} onChange={(v) => setGroup("hero", "buyer_card", v)} />
            <IconCardEditor title="Supplier Card" value={content.hero.supplier_card} path="hero.supplier_card" errors={errors} setErrors={setErrors} onChange={(v) => setGroup("hero", "supplier_card", v)} />
            <SubBlock title="Images" description="Uploads immediately as soon as a file is chosen.">
              <ImageUploadField label="Hero Background Image" {...imageProps("hero_background_image")} />
              <ImageUploadField label="Hero Desktop Dashboard Image" {...imageProps("hero_desktop_dashboard_image")} />
              <ImageUploadField label="Hero Mobile Dashboard Image" {...imageProps("hero_mobile_dashboard_image")} />
            </SubBlock>
          </Section>

          {/* TRUSTED BRANDS */}
          <Section title="Trusted Brands">
            <SubBlock title="Content">
              <Field
                label="Section Title"
                value={content.trusted_brands.section_title}
                limits={TEXT_RULES["trusted_brands.section_title"]}
                error={errors["trusted_brands.section_title"]}
                onChange={(v) => setValidatedGroup("trusted_brands", "section_title", v, "Section Title")}
              />
              {/* <Field
                label="Section Description"
                textarea
                value={content.trusted_brands.section_description}
                limits={TEXT_RULES["trusted_brands.section_description"]}
                error={errors["trusted_brands.section_description"]}
                onChange={(v) => setValidatedGroup("trusted_brands", "section_description", v, "Section Description")}
              /> */}
            </SubBlock>
            <Repeater
              title="Stats"
              items={content.trusted_brands.stats || []}
              fields={[["number"], ["label"]]}
              fieldRules={{ number: TEXT_RULES["trusted_brands.stat.number"], label: TEXT_RULES["trusted_brands.stat.label"] }}
              repeaterRules={REPEATER_RULES["trusted_brands.stats"]}
              path="trusted_brands.stats"
              errors={errors}
              setErrors={setErrors}
              onChange={(stats) => setGroup("trusted_brands", "stats", stats)}
            />
            <Repeater
              title="Highlights"
              items={content.trusted_brands.highlights || []}
              fields={[["label"]]}
              fieldRules={{ label: TEXT_RULES["trusted_brands.highlight.label"] }}
              repeaterRules={REPEATER_RULES["trusted_brands.highlights"]}
              path="trusted_brands.highlights"
              errors={errors}
              setErrors={setErrors}
              onChange={(highlights) => setGroup("trusted_brands", "highlights", highlights)}
            />
          </Section>

          {/* HOW PROCMAN WORKS */}
          <Section title="How Procman Works">
            <SubBlock title="Content">
              <Field
                label="Section Title"
                value={content.working.section_title}
                limits={TEXT_RULES["working.section_title"]}
                error={errors["working.section_title"]}
                onChange={(v) => setValidatedGroup("working", "section_title", v, "Section Title")}
              />
            </SubBlock>
            <Repeater
              title="Steps"
              items={content.working.steps || []}
              fields={[["title"], ["description", true]]}
              fieldRules={{ title: TEXT_RULES["working.step.title"], description: TEXT_RULES["working.step.description"] }}
              repeaterRules={REPEATER_RULES["working.steps"]}
              path="working.steps"
              errors={errors}
              setErrors={setErrors}
              onChange={(steps) => setGroup("working", "steps", steps)}
            />
          </Section>

          {/* BUYER / SUPPLIER */}
          <Section title="Built for Buyers and Suppliers">
            <SubBlock title="Content">
              <Field
                label="Section Title"
                value={content.buyer_supplier.section_title}
                limits={TEXT_RULES["buyer_supplier.section_title"]}
                error={errors["buyer_supplier.section_title"]}
                onChange={(v) => setValidatedGroup("buyer_supplier", "section_title", v, "Section Title")}
              />
              <Field
                label="Section Description"
                textarea
                value={content.buyer_supplier.section_description}
                limits={TEXT_RULES["buyer_supplier.section_description"]}
                error={errors["buyer_supplier.section_description"]}
                onChange={(v) => setValidatedGroup("buyer_supplier", "section_description", v, "Section Description")}
              />
            </SubBlock>
            <FeatureCardEditor title="Buyer Card" value={content.buyer_supplier.buyer_card} path="buyer_supplier.buyer_card" errors={errors} setErrors={setErrors} onChange={(v) => setGroup("buyer_supplier", "buyer_card", v)} />
            <FeatureCardEditor title="Supplier Card" value={content.buyer_supplier.supplier_card} path="buyer_supplier.supplier_card" errors={errors} setErrors={setErrors} onChange={(v) => setGroup("buyer_supplier", "supplier_card", v)} />
            <SubBlock title="Images" description="Uploads immediately as soon as a file is chosen.">
              <ImageUploadField label="Buyer Card Image" {...imageProps("buyer_card_image")} />
              <ImageUploadField label="Supplier Card Image" {...imageProps("supplier_card_image")} />
            </SubBlock>
          </Section>

          {/* PLATFORM FEATURES */}
          <Section title="Platform Features">
            <SubBlock title="Content">
              <Field
                label="Section Badge"
                value={content.platform_features.section_badge}
                limits={TEXT_RULES["platform_features.section_badge"]}
                error={errors["platform_features.section_badge"]}
                onChange={(v) => setValidatedGroup("platform_features", "section_badge", v, "Section Badge")}
              />
              <Field
                label="Section Title"
                value={content.platform_features.section_title}
                limits={TEXT_RULES["platform_features.section_title"]}
                error={errors["platform_features.section_title"]}
                onChange={(v) => setValidatedGroup("platform_features", "section_title", v, "Section Title")}
              />
              <Field
                label="Section Description"
                textarea
                value={content.platform_features.section_description}
                limits={TEXT_RULES["platform_features.section_description"]}
                error={errors["platform_features.section_description"]}
                onChange={(v) => setValidatedGroup("platform_features", "section_description", v, "Section Description")}
              />
            </SubBlock>
            <Repeater
              title="Features"
              items={content.platform_features.features || []}
              fields={[["title"], ["description", true]]}
              fieldRules={{ title: TEXT_RULES["platform_features.feature.title"], description: TEXT_RULES["platform_features.feature.description"] }}
              repeaterRules={REPEATER_RULES["platform_features.features"]}
              path="platform_features.features"
              errors={errors}
              setErrors={setErrors}
              onChange={(features) => setGroup("platform_features", "features", features)}
            />
          </Section>

          {/* COMPARISON */}
          <Section title="Why Procman (Comparison)">
            <SubBlock title="Content">
              <Field
                label="Section Badge"
                value={content.comparision.section_badge}
                limits={TEXT_RULES["comparision.section_badge"]}
                error={errors["comparision.section_badge"]}
                onChange={(v) => setValidatedGroup("comparision", "section_badge", v, "Section Badge")}
              />
              <Field
                label="Section Title"
                value={content.comparision.section_title}
                limits={TEXT_RULES["comparision.section_title"]}
                error={errors["comparision.section_title"]}
                onChange={(v) => setValidatedGroup("comparision", "section_title", v, "Section Title")}
              />
            </SubBlock>
            <SubBlock title="Procman Column">
              <Field
                label="Title"
                value={content.comparision.procman.title}
                limits={TEXT_RULES["comparision.procman.title"]}
                error={errors["comparision.procman.title"]}
                onChange={(v) => {
                  setGroup("comparision", "procman", { ...content.comparision.procman, title: v });
                  setErrorEntry(setErrors, "comparision.procman.title", validateText(v, TEXT_RULES["comparision.procman.title"], "Title"));
                }}
              />
            </SubBlock>
            <StringListEditor
              title="Procman Points"
              items={content.comparision.procman.points || []}
              path="comparision.procman.points"
              rules={TEXT_RULES["comparision.procman.point"]}
              repeaterRules={REPEATER_RULES["comparision.procman.points"]}
              errors={errors}
              setErrors={setErrors}
              onChange={(points) => setGroup("comparision", "procman", { ...content.comparision.procman, points })}
            />
            <SubBlock title="Traditional Column">
              <Field
                label="Title"
                value={content.comparision.traditional.title}
                limits={TEXT_RULES["comparision.traditional.title"]}
                error={errors["comparision.traditional.title"]}
                onChange={(v) => {
                  setGroup("comparision", "traditional", { ...content.comparision.traditional, title: v });
                  setErrorEntry(setErrors, "comparision.traditional.title", validateText(v, TEXT_RULES["comparision.traditional.title"], "Title"));
                }}
              />
            </SubBlock>
            <StringListEditor
              title="Traditional Points"
              items={content.comparision.traditional.points || []}
              path="comparision.traditional.points"
              rules={TEXT_RULES["comparision.traditional.point"]}
              repeaterRules={REPEATER_RULES["comparision.traditional.points"]}
              errors={errors}
              setErrors={setErrors}
              onChange={(points) => setGroup("comparision", "traditional", { ...content.comparision.traditional, points })}
            />
          </Section>

          {/* ENTERPRISE SECURITY */}
          <Section title="Enterprise Grade Security">
            <SubBlock title="Content">
              <Field
                label="Section Title"
                value={content.enterprise_security.section_title}
                limits={TEXT_RULES["enterprise_security.section_title"]}
                error={errors["enterprise_security.section_title"]}
                onChange={(v) => setValidatedGroup("enterprise_security", "section_title", v, "Section Title")}
              />
            </SubBlock>
            <Repeater
              title="Security Features"
              items={content.enterprise_security.security_features || []}
              fields={[["title"], ["description", true]]}
              fieldRules={{ title: TEXT_RULES["enterprise_security.feature.title"], description: TEXT_RULES["enterprise_security.feature.description"] }}
              repeaterRules={REPEATER_RULES["enterprise_security.security_features"]}
              path="enterprise_security.security_features"
              errors={errors}
              setErrors={setErrors}
              onChange={(security_features) => setGroup("enterprise_security", "security_features", security_features)}
            />
            <SubBlock title="Images" description="Uploads immediately as soon as a file is chosen.">
              <ImageUploadField label="Security Feature 1 Image" {...imageProps("security_feature_1_image")} />
              <ImageUploadField label="Security Feature 2 Image" {...imageProps("security_feature_2_image")} />
              <ImageUploadField label="Security Feature 3 Image" {...imageProps("security_feature_3_image")} />
            </SubBlock>
          </Section>

          {/* INDUSTRIES */}
          <Section title="Industries">
            <SubBlock title="Content">
              <Field
                label="Section Title"
                value={content.industries.section_title}
                limits={TEXT_RULES["industries.section_title"]}
                error={errors["industries.section_title"]}
                onChange={(v) => setValidatedGroup("industries", "section_title", v, "Section Title")}
              />
            </SubBlock>
          </Section>

          {/* CASE STUDIES */}
          <Section title="Case Studies / Success Stories">
            <SubBlock title="Content">
              <Field
                label="Section Title"
                value={content.case_studies.section_title}
                limits={TEXT_RULES["case_studies.section_title"]}
                error={errors["case_studies.section_title"]}
                onChange={(v) => setValidatedGroup("case_studies", "section_title", v, "Section Title")}
              />
              <Field
                label="Section Description"
                value={content.case_studies.section_description}
                limits={TEXT_RULES["case_studies.section_description"]}
                error={errors["case_studies.section_description"]}
                onChange={(v) => setValidatedGroup("case_studies", "section_description", v, "Section description")}
              />
            </SubBlock>
          </Section>

          {/* TESTIMONIALS */}
          <Section title="Testimonials">
            <SubBlock title="Content">
              <Field
                label="Section Title"
                value={content.testimonials.section_title}
                limits={TEXT_RULES["testimonials.section_title"]}
                error={errors["testimonials.section_title"]}
                onChange={(v) => setValidatedGroup("testimonials", "section_title", v, "Section Title")}
              />
              {/* <Field
                label="Section Description"
                value={content.testimonials.section_description}
                limits={TEXT_RULES["testimonials.section_description"]}
                error={errors["testimonials.section_description"]}
                onChange={(v) => setValidatedGroup("testimonials", "section_description", v, "Section Description")}
              /> */}
            </SubBlock>
          </Section>

          {/* FAQ */}
          <Section title="FAQ">
            <SubBlock title="Content">
              <Field
                label="Section Title"
                value={content.faq.section_title}
                limits={TEXT_RULES["faq.section_title"]}
                error={errors["faq.section_title"]}
                onChange={(v) => setValidatedGroup("faq", "section_title", v, "Section Title")}
              />
            </SubBlock>
            <Repeater
              title="FAQs"
              items={content.faq.faqs || []}
              fields={[["question"], ["answer", true]]}
              fieldRules={{ question: TEXT_RULES["faq.faq.question"], answer: TEXT_RULES["faq.faq.answer"] }}
              repeaterRules={REPEATER_RULES["faq.faqs"]}
              path="faq.faqs"
              errors={errors}
              setErrors={setErrors}
              onChange={(faqs) => setGroup("faq", "faqs", faqs)}
            />
          </Section>

          {/* CTA */}
          <Section title="CTA Section">
            <SubBlock title="Content">
              <Field
                label="Section Title"
                value={content.cta.section_title}
                limits={TEXT_RULES["cta.section_title"]}
                error={errors["cta.section_title"]}
                onChange={(v) => setValidatedGroup("cta", "section_title", v, "Section Title")}
              />
              <Field
                label="Section Description"
                textarea
                value={content.cta.section_description}
                limits={TEXT_RULES["cta.section_description"]}
                error={errors["cta.section_description"]}
                onChange={(v) => setValidatedGroup("cta", "section_description", v, "Section Description")}
              />
              <CtaFields
                label="Primary CTA"
                value={content.cta.primary_cta}
                pathPrefix="cta.primary_cta"
                labelRules={TEXT_RULES["cta.primary_cta.label"]}
                errors={errors}
                setErrors={setErrors}
                onChange={(v) => setGroup("cta", "primary_cta", v)}
              />
              <CtaFields
                label="Secondary CTA"
                value={content.cta.secondary_cta}
                pathPrefix="cta.secondary_cta"
                labelRules={TEXT_RULES["cta.secondary_cta.label"]}
                errors={errors}
                setErrors={setErrors}
                onChange={(v) => setGroup("cta", "secondary_cta", v)}
              />
            </SubBlock>
            <Repeater
              title="Benefits"
              items={content.cta.benefits || []}
              fields={[["text"]]}
              fieldRules={{ text: TEXT_RULES["cta.benefit.text"] }}
              repeaterRules={REPEATER_RULES["cta.benefits"]}
              path="cta.benefits"
              errors={errors}
              setErrors={setErrors}
              onChange={(benefits) => setGroup("cta", "benefits", benefits)}
            />
          </Section>

          {overlayUrl && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Image preview"
              onClick={() => setOverlayUrl("")}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 1000,
                padding: 24,
                background: "rgba(15,23,42,.82)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={overlayUrl}
                alt="Full preview"
                onClick={(event) => event.stopPropagation()}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  borderRadius: 10,
                  background: "#fff",
                }}
              />
              <button
                type="button"
                onClick={() => setOverlayUrl("")}
                style={{
                  position: "fixed",
                  top: 18,
                  right: 18,
                  margin: 0,
                  border: "1px solid #fff",
                  background: "rgba(15,23,42,.65)",
                  color: "#fff",
                  fontSize: 18,
                  padding: "8px 12px",
                }}
              >
                ×
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}